// ============================================================
// A HORDE ON THE PLATE (plan body-map P5; the canvas's crowd): always the same
// twenty members of the horde's figure, the share still standing being the
// share of magnitude left - so a horde of 60 at half strength looks like a
// horde of 20 at half strength. Four staggered rows of five, the back rows
// smaller and darker; the front rows stand first and fall last.
//
// The layout's jitter is a fixed hash of the slot, never Math.random: a redraw
// keeps every member where it stood (and gotcha 5 keeps the dice untouched).
//
// Pure. Zero React dependency.
// ============================================================

import { hash01 } from './hash';

/** How many members the crowd is drawn as. */
const CROWD = 20;
const ROWS = 4;
const PER_ROW = 5;

/** One member's place in the plate's frame: its feet at (x, y), its size, and how much of the line colour it wears. */
interface CrowdSlot {
  x: number;
  y: number;
  /** Of the full figure (2123 x 3000). */
  scale: number;
  /** Percent of the line colour mixed into the ground: the back rows fade into it. */
  tint: number;
}

/** The canvas's jitter: a fixed hash of a slot and a key. */
const jitter = hash01;

function slotAt(row: number, col: number, i: number): CrowdSlot {
  const depth = ROWS - 1 - row; // 3 = the front row
  return {
    x: 1061 + (col - 2) * (380 + 70 * depth) + (row % 2 ? 170 : 0) + (jitter(i, 2) - 0.5) * 80,
    y: 1500 + depth * 470 + (jitter(i, 1) - 0.5) * 60,
    scale: 0.24 + 0.05 * depth,
    tint: 55 + 15 * depth,
  };
}

/** The twenty slots, front row first (so the first N are the nearest). */
export const CROWD_SLOTS: readonly CrowdSlot[] = Array.from({ length: CROWD }, (_, i) =>
  slotAt(Math.floor(i / PER_ROW), i % PER_ROW, i),
);

/** A horde with no maximum on its sheet is read against this (the old map's default), so it still thins. */
const DEFAULT_MAX = 30;

/** How many members stand: the share of magnitude left, at least one while any is left. */
export function membersStanding(magnitude: number, max: number): number {
  if (magnitude <= 0) return 0;
  const of = max > 0 ? max : DEFAULT_MAX;
  return Math.min(CROWD, Math.max(1, Math.round((CROWD * magnitude) / of)));
}

/** Slots in drawing order: back rows first, so the nearer cover them (a new array). */
export const byDepth = (slots: readonly number[]) => [...slots].sort((a, b) => CROWD_SLOTS[a].y - CROWD_SLOTS[b].y);

/** The standing members' slots, in drawing order. */
export function standingSlots(magnitude: number, max: number): number[] {
  return byDepth(Array.from({ length: membersStanding(magnitude, max) }, (_, i) => i));
}

/** The members a fall in magnitude felled: the back-most of those who stood (the front rows fall last). */
export function felledSlots(from: number, to: number): number[] {
  return Array.from({ length: Math.max(0, from - to) }, (_, k) => to + k);
}

/** The members' boxes together, in frame units (each member is the figure, 2123 x 3000, at its slot's scale). */
export function crowdBox(slots: readonly number[]): [number, number, number, number] {
  const boxes = slots.map((i) => {
    const { x, y, scale } = CROWD_SLOTS[i];
    return [x - 1061 * scale, y - 3000 * scale, x + 1062 * scale, y];
  });
  return [
    Math.min(...boxes.map((b) => b[0])),
    Math.min(...boxes.map((b) => b[1])),
    Math.max(...boxes.map((b) => b[2])),
    Math.max(...boxes.map((b) => b[3])),
  ];
}

/**
 * Where a crowd's words stand and the frame round it (plan plate-unboxed: every body fills its tab): fitted to the
 * whole crowd of twenty - so the frame never jumps as the horde thins - with its MAG stencil (300 px type) just above
 * the back row's heads and its strip (80 px) just under the front row's feet, and a margin.
 */
export const CROWD_LAYOUT = (() => {
  const [x0, y0, x1, y1] = crowdBox(CROWD_SLOTS.map((_, i) => i));
  const magY = y0 - 140;
  const stripY = y1 + 130;
  const top = magY - 240;
  const bottom = stripY + 30;
  const margin = 60;
  return {
    frame: { x: x0 - margin, y: top - margin, w: x1 - x0 + 2 * margin, h: bottom - top + 2 * margin },
    mid: (x0 + x1) / 2,
    magY,
    stripY,
  };
})();
